import { CorrugatedSheetsCalculationConfig } from "./corrugatedSheetCalculation";
import {
    OndulineColor,
    ProductLine,
    ProductLineType,
} from "./corrugatedSheetCalculator.types";

const BargeFormula = (width: number, x: number, y: number) =>
    ((width / 100) * 2) / (x - y);

const CapFormula = (length: number, x: number, y: number) =>
    length / 100 / (x - y);

const MouseFormula = (length: number, x: number) => length / 100 / x;

export const CorrugatedSheetsCalculationConfiguration: Record<
    ProductLineType,
    CorrugatedSheetsCalculationConfig
> = {
    [ProductLine.Onduvilla]: {
        sheets: [
            {
                color: OndulineColor.Black,
                sku: "358695",
            },
            {
                color: OndulineColor.Red,
                sku: "356685",
            },
        ],
        fixtures: [
            {
                color: OndulineColor.Black,
                sku: "774081",
            },
            {
                color: OndulineColor.Red,
                sku: "774082",
            },
        ],
        accessories: [
            // windveer
            {
                color: OndulineColor.Black,
                sku: "501282",
                amountFormula: (_length: number, width: number) =>
                    BargeFormula(width, 1.04, 0.08),
                requiredFixtures: 6,
                twoSideMultiplier: 2,
            },
            {
                color: OndulineColor.Red,
                sku: "501281",
                amountFormula: (_length: number, width: number) =>
                    BargeFormula(width, 1.04, 0.08),
                requiredFixtures: 6,
                twoSideMultiplier: 2,
            },
            // slim cap
            {
                color: OndulineColor.Black,
                sku: "501276",
                amountFormula: (length: number) =>
                    CapFormula(length, 1.06, 0.08) - 2,
                requiredFixtures: 12,
                twoSideMultiplier: 1,
            },
            {
                color: OndulineColor.Red,
                sku: "501275",
                amountFormula: (length: number) =>
                    CapFormula(length, 1.06, 0.08) - 2,
                requiredFixtures: 12,
                twoSideMultiplier: 1,
            },
            // closure cap
            {
                color: OndulineColor.Black,
                sku: "501279",
                amountFormula: () => 2,
                condition: activeAcc => activeAcc.some(a => a.sku === "501276"),
                requiredFixtures: 12,
                twoSideMultiplier: 1,
            },
            {
                color: OndulineColor.Red,
                sku: "501278",
                amountFormula: () => 2,
                condition: activeAcc => activeAcc.some(a => a.sku === "501275"),
                requiredFixtures: 12,
                twoSideMultiplier: 1,
            },
            // ventilatiemouse
            {
                color: undefined,
                sku: "501273",
                amountFormula: (length: number) => MouseFormula(length, 0.985),
                requiredFixtures: 0,
                twoSideMultiplier: 2,
            },
        ],
        slopes: [{ division: 0.312, fixtures: 5 }],
    },
    [ProductLine.OnduLineEasyfix]: {
        sheets: [
            {
                color: OndulineColor.Brown,
                sku: "774094",
            },
        ],
        fixtures: [
            {
                color: OndulineColor.Brown,
                sku: "774083",
            },
        ],
        accessories: [
            // windveer
            {
                color: OndulineColor.Brown,
                sku: "774085",
                amountFormula: (_length: number, width: number) =>
                    BargeFormula(width, 1, 0.125),
                requiredFixtures: 6,
                twoSideMultiplier: 2,
            },
            // nok
            {
                color: OndulineColor.Brown,
                sku: "774092",
                amountFormula: (length: number) => CapFormula(length, 1, 0.12),
                requiredFixtures: 12,
                twoSideMultiplier: 1,
            },
        ],
        slopes: [
            { below: 0.15, division: 1.34, warning: true, fixtures: 20 },
            { above: 0.15, division: 1.34, fixtures: 15 },
        ],
    },
    [ProductLine.OndulineOndalux]: {
        sheets: [
            {
                color: OndulineColor.Black,
                sku: "89814",
            },
            {
                color: OndulineColor.Red,
                sku: "91165",
            },
            {
                color: OndulineColor.Green,
                sku: "91164",
            },
        ],
        fixtures: [
            {
                color: OndulineColor.Black,
                sku: "89817",
            },
            {
                color: OndulineColor.Red,
                sku: "91171",
            },
            {
                color: OndulineColor.Green,
                sku: "91170",
            },
        ],
        accessories: [
            // windveer
            {
                color: OndulineColor.Black,
                sku: "774084",
                amountFormula: (_length: number, width: number) =>
                    BargeFormula(width, 1, 0.125),
                requiredFixtures: 6,
                twoSideMultiplier: 2,
            },
            // nok
            {
                color: OndulineColor.Black,
                sku: "774091",
                amountFormula: (length: number) => CapFormula(length, 1, 0.12),
                requiredFixtures: 12,
                twoSideMultiplier: 1,
            },
        ],
        slopes: [
            { below: 0.09, division: 1.29, warning: true, fixtures: 20 },
            { above: 0.09, below: 0.17, division: 1.29, fixtures: 20 },
            { above: 0.17, below: 0.27, division: 1.54, fixtures: 20 },
            { above: 0.27, division: 1.56, fixtures: 20 },
        ],
    },
    [ProductLine.OndulineEasyline]: {
        sheets: [
            {
                color: OndulineColor.Black,
                sku: "514805",
            },
        ],
        fixtures: [{ color: OndulineColor.Black, sku: "89817" }],
        accessories: [
            // windveer
            {
                color: OndulineColor.Black,
                sku: "774084",
                amountFormula: (_length: number, width: number) =>
                    BargeFormula(width, 1, 0.125),
                requiredFixtures: 6,
                twoSideMultiplier: 2,
            },
            // nok
            {
                color: OndulineColor.Black,
                sku: "774091",
                amountFormula: (length: number) => CapFormula(length, 1, 0.12),
                requiredFixtures: 12,
                twoSideMultiplier: 1,
            },
        ],
        slopes: [
            { below: 0.08, division: 0.4, warning: true, fixtures: 11 },
            { above: 0.08, below: 0.27, division: 0.4, fixtures: 11 },
            { above: 0.27, division: 0.57, fixtures: 11 },
        ],
    },
};
